import { Truck } from 'lucide-react';
import { useState } from 'react';
import { ErrorBanner, Field, NativeSelect } from '@/components/form';
import { DateInput } from '@/components/date-input';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ApiError } from '@/lib/api';
import { useMe } from '@/lib/clients';
import { formatDateTime } from '@/lib/format';
import {
  EWB_CANCEL_REASONS,
  EWB_MODES,
  EWB_VEHICLE_REASONS,
  GST_STATES,
  ewbCancelReasonLabel,
  ewbModeLabel,
  ewbSubSupplyLabel,
  ewbVehicleReasonLabel,
  testConnectionHint,
  useEwayBill,
  useEwayBillAction,
  type EwbTransport,
} from '@/lib/gst';
import { ewbGenerateErrors, ewbTransportBody, ewbVehicleErrors } from '@/lib/gst-forms';
import { isIndia, orgLocale } from '@/lib/org-locale';
import { cn } from '@/lib/utils';
import { ltr } from '@/lib/arabic';
import { useT } from '@/lib/i18n';

const errorText = (e: unknown) =>
  e instanceof ApiError
    ? Array.isArray(e.details) && e.details.length
      ? (e.details as Array<{ message: string }>).map((d) => d.message)
      : [e.message]
    : [String(e)];

/**
 * E-way bill strip on an approved India invoice or a delivery challan (document 06 T10): whether
 * one is needed (₹50,000, or inter-state job work), the bill once generated with its validity and
 * vehicle, and Generate, Update vehicle (Part B) and Cancel (within 24 hours). Nothing for UAE
 * clients. While a bill is active the document cannot be edited, voided or cancelled.
 */
export function EwayBillPanel({
  documentId,
  number,
}: {
  documentId: string;
  number: string | null;
}) {
  const india = isIndia();
  const t = useT();
  const q = useEwayBill(documentId, india);
  const me = useMe();
  const [dialog, setDialog] = useState<'generate' | 'vehicle' | 'cancel' | null>(null);
  if (!india || !q.data) return null;
  const { required, ewayBill: bill, irn, defaults, goods } = q.data;
  const staff = me.data?.isStaff ?? false;
  const active = bill?.status === 'active' ? bill : null;
  const expired = Boolean(active?.validUntil && new Date(active.validUntil) < new Date());

  return (
    <div
      data-testid="ewb-panel"
      className={cn(
        'flex flex-wrap items-center gap-3 rounded-md border px-4 py-2.5 text-sm',
        !active && required.required ? 'border-[#FCD34D] bg-[#FFFBEB]' : 'bg-card',
      )}
    >
      <span className="inline-flex items-center gap-1.5 font-medium">
        <Truck className="size-4" aria-hidden /> {t('taxPages.ewayBill.title')}
      </span>
      {active?.provider === 'mock' ? (
        <span
          className="rounded-full bg-[#FEF3F2] px-2 py-0.5 text-xs font-medium text-[#B42318]"
          title={testConnectionHint()}
          data-testid="ewb-test"
        >
          {t('taxPages.ewayBill.testBill')}
        </span>
      ) : null}
      {active ? (
        <span className="min-w-48 flex-1 text-muted-foreground">
          <span className="tnum text-foreground" data-testid="ewb-number">
            {t('taxPages.ewayBill.ewbNo', { number: active.ewbNo })}
          </span>{' '}
          · {formatDateTime(active.ewbDate)}
          {active.generatedVia === 'irn' ? ` · ${t('taxPages.ewayBill.throughIrp')}` : ''}
          <span className="block">
            {active.validUntil
              ? expired
                ? t('taxPages.ewayBill.expiredOn', { when: formatDateTime(active.validUntil) })
                : t('taxPages.ewayBill.validUntil', { when: formatDateTime(active.validUntil) })
              : t('taxPages.ewayBill.partAOnlyHint')}
            {active.vehicleNo
              ? ` · ${t('taxPages.ewayBill.vehicle', { number: ltr(active.vehicleNo) })}`
              : ''}
            {active.transDocNo
              ? ` · ${t('taxPages.ewayBill.transportDoc', { number: ltr(active.transDocNo) })}`
              : ''}
            {active.transporterName ? ` · ${active.transporterName}` : ''}
          </span>
        </span>
      ) : (
        <span className="min-w-48 flex-1 text-muted-foreground" data-testid="ewb-required">
          {bill?.status === 'cancelled'
            ? `${t('taxPages.ewayBill.wasCancelled', { number: ltr(bill.ewbNo) })} `
            : ''}
          {required.required ? (
            <span className="text-foreground">{t('taxPages.ewayBill.required')} </span>
          ) : null}
          {required.reason}.
        </span>
      )}
      {staff && !active && goods ? (
        <Button
          size="sm"
          variant={required.required ? 'default' : 'outline'}
          onClick={() => setDialog('generate')}
        >
          {t('taxPages.ewayBill.generate')}
        </Button>
      ) : null}
      {staff && active && !expired ? (
        <Button size="sm" variant="outline" onClick={() => setDialog('vehicle')}>
          {active.validUntil
            ? t('taxPages.ewayBill.updateVehicle')
            : t('taxPages.ewayBill.addVehicle')}
        </Button>
      ) : null}
      {staff && active?.cancellable ? (
        <Button size="sm" variant="ghost" onClick={() => setDialog('cancel')}>
          {t('taxPages.ewayBill.cancelBill')}
        </Button>
      ) : null}
      {dialog === 'generate' ? (
        <GenerateDialog
          documentId={documentId}
          number={number}
          irn={irn}
          subSupply={`${ewbSubSupplyLabel(defaults.subSupplyType)}${
            defaults.subSupplyDesc ? ` (${defaults.subSupplyDesc})` : ''
          }`}
          pins={ltr(`${defaults.fromPincode || '—'} → ${defaults.toPincode || '—'}`)}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog === 'vehicle' ? (
        <VehicleDialog documentId={documentId} onClose={() => setDialog(null)} />
      ) : null}
      {dialog === 'cancel' && active ? (
        <CancelDialog
          documentId={documentId}
          ewbNo={active.ewbNo}
          onClose={() => setDialog(null)}
        />
      ) : null}
    </div>
  );
}

function GenerateDialog({
  documentId,
  number,
  irn,
  subSupply,
  pins,
  onClose,
}: {
  documentId: string;
  number: string | null;
  irn: string | null;
  subSupply: string;
  pins: string;
  onClose: () => void;
}) {
  const t = useT();
  const action = useEwayBillAction(documentId);
  const [tr, setT] = useState<EwbTransport>({
    distanceKm: 0,
    transMode: 1,
    vehicleNo: '',
    vehicleType: 'R',
    transporterId: '',
    transporterName: '',
    transDocNo: '',
    transDocDate: '',
  });
  const [errors, setErrors] = useState<string[]>([]);
  const set = <K extends keyof EwbTransport>(k: K, v: EwbTransport[K]) =>
    setT((p) => ({ ...p, [k]: v }));
  const road = tr.transMode === 1;
  const submit = () => {
    const problems = ewbGenerateErrors(tr);
    setErrors(problems);
    if (problems.length) return;
    action.mutate(
      { kind: 'generate', transport: ewbTransportBody(tr) },
      {
        onSuccess: (b) => {
          toast(
            number
              ? t('taxPages.ewayBill.toastGenerated', { ewbNo: ltr(b.ewbNo), number: ltr(number) })
              : t('taxPages.ewayBill.toastGeneratedDoc', { ewbNo: ltr(b.ewbNo) }),
          );
          onClose();
        },
        onError: (e) => setErrors(errorText(e)),
      },
    );
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('taxPages.ewayBill.generate')}</DialogTitle>
          <DialogDescription>
            {irn
              ? t('taxPages.ewayBill.generateHintIrn', { subSupply, pins })
              : t('taxPages.ewayBill.generateHint', { subSupply, pins })}
          </DialogDescription>
        </DialogHeader>
        {errors.length ? (
          <ErrorBanner>
            <ul className="list-disc ps-4">
              {errors.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          </ErrorBanner>
        ) : null}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label={t('taxPages.ewayBill.distance')} hint={t('taxPages.ewayBill.distanceHint')}>
            {(id) => (
              <Input
                id={id}
                className="tnum"
                inputMode="numeric"
                maxLength={4}
                value={String(tr.distanceKm)}
                onChange={(e) => set('distanceKm', Number(e.target.value.replace(/\D/g, '') || 0))}
              />
            )}
          </Field>
          <Field label={t('taxPages.gst.mode')}>
            {(id) => (
              <NativeSelect
                id={id}
                value={String(tr.transMode ?? 1)}
                onChange={(e) => set('transMode', Number(e.target.value))}
              >
                {EWB_MODES.map(([v]) => (
                  <option key={v} value={v}>
                    {ewbModeLabel(v)}
                  </option>
                ))}
              </NativeSelect>
            )}
          </Field>
          {road ? (
            <>
              <Field label={t('taxPages.ewayBill.vehicleNo')}>
                {(id) => (
                  <Input
                    id={id}
                    placeholder="MH12AB1234"
                    maxLength={20}
                    value={tr.vehicleNo ?? ''}
                    onChange={(e) => set('vehicleNo', e.target.value.toUpperCase())}
                  />
                )}
              </Field>
              <Field label={t('taxPages.ewayBill.vehicleType')}>
                {(id) => (
                  <NativeSelect
                    id={id}
                    value={tr.vehicleType}
                    onChange={(e) => set('vehicleType', e.target.value as 'R' | 'O')}
                  >
                    <option value="R">{t('taxPages.ewayBill.vehicleRegular')}</option>
                    <option value="O">{t('taxPages.ewayBill.vehicleOdc')}</option>
                  </NativeSelect>
                )}
              </Field>
            </>
          ) : (
            <>
              <Field
                label={t('taxPages.ewayBill.transDocNo')}
                hint={t('taxPages.ewayBill.transDocNoHint')}
              >
                {(id) => (
                  <Input
                    id={id}
                    maxLength={15}
                    value={tr.transDocNo ?? ''}
                    onChange={(e) => set('transDocNo', e.target.value)}
                  />
                )}
              </Field>
              <Field label={t('taxPages.ewayBill.transDocDate')}>
                {(id) => (
                  <DateInput
                    id={id}
                    value={tr.transDocDate ?? ''}
                    onChange={(v) => set('transDocDate', v)}
                  />
                )}
              </Field>
            </>
          )}
          <Field
            label={t('taxPages.ewayBill.transporterId')}
            hint={t('taxPages.ewayBill.transporterIdHint')}
          >
            {(id) => (
              <Input
                id={id}
                maxLength={15}
                value={tr.transporterId ?? ''}
                onChange={(e) => set('transporterId', e.target.value.toUpperCase())}
              />
            )}
          </Field>
          <Field label={t('taxPages.ewayBill.transporterName')}>
            {(id) => (
              <Input
                id={id}
                maxLength={100}
                value={tr.transporterName ?? ''}
                onChange={(e) => set('transporterName', e.target.value)}
              />
            )}
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button disabled={action.isPending} onClick={submit}>
            {action.isPending
              ? t('taxPages.ewayBill.generating')
              : t('taxPages.ewayBill.generateButton')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function VehicleDialog({ documentId, onClose }: { documentId: string; onClose: () => void }) {
  const t = useT();
  const action = useEwayBillAction(documentId);
  const [u, setU] = useState({
    transMode: 1,
    vehicleNo: '',
    transDocNo: '',
    transDocDate: '',
    fromPlace: '',
    fromState: orgLocale().stateCode ?? '',
    reasonCode: 4,
    reasonRemark: '',
    distanceKm: 0,
  });
  const [errors, setErrors] = useState<string[]>([]);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('taxPages.ewayBill.updateVehicleTitle')}</DialogTitle>
          <DialogDescription>{t('taxPages.ewayBill.updateVehicleHint')}</DialogDescription>
        </DialogHeader>
        {errors.length ? (
          <ErrorBanner>
            <ul className="list-disc ps-4">
              {errors.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          </ErrorBanner>
        ) : null}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label={t('taxPages.ewayBill.reason')}>
            {(id) => (
              <NativeSelect
                id={id}
                value={String(u.reasonCode)}
                onChange={(e) => setU((p) => ({ ...p, reasonCode: Number(e.target.value) }))}
              >
                {EWB_VEHICLE_REASONS.map(([v]) => (
                  <option key={v} value={v}>
                    {ewbVehicleReasonLabel(v)}
                  </option>
                ))}
              </NativeSelect>
            )}
          </Field>
          <Field label={t('taxPages.gst.mode')}>
            {(id) => (
              <NativeSelect
                id={id}
                value={String(u.transMode)}
                onChange={(e) => setU((p) => ({ ...p, transMode: Number(e.target.value) }))}
              >
                {EWB_MODES.map(([v]) => (
                  <option key={v} value={v}>
                    {ewbModeLabel(v)}
                  </option>
                ))}
              </NativeSelect>
            )}
          </Field>
          {u.transMode === 1 ? (
            <Field label={t('taxPages.ewayBill.vehicleNoRequired')}>
              {(id) => (
                <Input
                  id={id}
                  maxLength={20}
                  value={u.vehicleNo}
                  onChange={(e) => setU((p) => ({ ...p, vehicleNo: e.target.value.toUpperCase() }))}
                />
              )}
            </Field>
          ) : (
            <Field label={t('taxPages.ewayBill.transDocNoRequired')}>
              {(id) => (
                <Input
                  id={id}
                  maxLength={15}
                  value={u.transDocNo}
                  onChange={(e) => setU((p) => ({ ...p, transDocNo: e.target.value }))}
                />
              )}
            </Field>
          )}
          <Field label={t('taxPages.ewayBill.distanceLeft')}>
            {(id) => (
              <Input
                id={id}
                className="tnum"
                inputMode="numeric"
                maxLength={4}
                value={String(u.distanceKm)}
                onChange={(e) =>
                  setU((p) => ({
                    ...p,
                    distanceKm: Number(e.target.value.replace(/\D/g, '') || 0),
                  }))
                }
              />
            )}
          </Field>
          <Field label={t('taxPages.ewayBill.fromPlace')}>
            {(id) => (
              <Input
                id={id}
                maxLength={50}
                value={u.fromPlace}
                onChange={(e) => setU((p) => ({ ...p, fromPlace: e.target.value }))}
              />
            )}
          </Field>
          <Field label={t('taxPages.ewayBill.fromState')}>
            {(id) => (
              <NativeSelect
                id={id}
                value={u.fromState}
                onChange={(e) => setU((p) => ({ ...p, fromState: e.target.value }))}
              >
                <option value="" />
                {GST_STATES.map(([code, name]) => (
                  <option key={code} value={code}>
                    {code} · {name}
                  </option>
                ))}
              </NativeSelect>
            )}
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            disabled={action.isPending}
            onClick={() => {
              const problems = ewbVehicleErrors(u);
              setErrors(problems);
              if (problems.length) return;
              action.mutate(
                {
                  kind: 'vehicle',
                  update: {
                    ...u,
                    vehicleNo: u.transMode === 1 ? u.vehicleNo.trim() || null : null,
                    transDocNo: u.transMode === 1 ? null : u.transDocNo.trim() || null,
                    transDocDate: u.transDocDate || null,
                    fromPlace: u.fromPlace.trim(),
                    reasonRemark: u.reasonRemark || null,
                  },
                },
                {
                  onSuccess: (b) => {
                    toast(t('taxPages.ewayBill.toastUpdated', { ewbNo: ltr(b.ewbNo) }));
                    onClose();
                  },
                  onError: (e) => setErrors(errorText(e)),
                },
              );
            }}
          >
            {t('taxPages.ewayBill.update')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CancelDialog({
  documentId,
  ewbNo,
  onClose,
}: {
  documentId: string;
  ewbNo: string;
  onClose: () => void;
}) {
  const t = useT();
  const action = useEwayBillAction(documentId);
  const [reasonCode, setReasonCode] = useState(3);
  const [remark, setRemark] = useState('');
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('taxPages.ewayBill.cancelTitle', { ewbNo })}</DialogTitle>
          <DialogDescription>{t('taxPages.ewayBill.cancelHint')}</DialogDescription>
        </DialogHeader>
        <Field label={t('taxPages.ewayBill.reason')}>
          {(id) => (
            <NativeSelect
              id={id}
              value={String(reasonCode)}
              onChange={(e) => setReasonCode(Number(e.target.value))}
            >
              {EWB_CANCEL_REASONS.map(([v]) => (
                <option key={v} value={v}>
                  {ewbCancelReasonLabel(v)}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
        <Field label={t('taxPages.ewayBill.remark')}>
          {(id) => (
            <Input
              id={id}
              value={remark}
              maxLength={50}
              onChange={(e) => setRemark(e.target.value)}
            />
          )}
        </Field>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('taxPages.ewayBill.keepIt')}
          </Button>
          <Button
            disabled={remark.trim().length < 3 || action.isPending}
            onClick={() =>
              action.mutate(
                { kind: 'cancel', reasonCode, remark: remark.trim() },
                {
                  onSuccess: () => {
                    toast(t('taxPages.ewayBill.toastCancelled', { ewbNo: ltr(ewbNo) }));
                    onClose();
                  },
                  onError: (e) => toast(e.message, { tone: 'error' }),
                },
              )
            }
          >
            {t('taxPages.ewayBill.cancelBill')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
